import { useState } from "react";
import AuthShell from "./AuthShell";
import AuthField from "./AuthField";
import Button from "../../ui/Button";
import { ageFromDob } from "../../../utils/clientAppUtils";
import a from "./auth.module.css";

const EMPTY_CHILD = { fullName: "", date_of_birth: "" };

function ModePicker({ onPick }) {
  return (
    <div className={a.regLinkModeList}>
      <button
        type="button"
        className={a.regLinkModeBtn}
        onClick={() => onPick("family")}
      >
        <span className={a.regLinkModeIcon} aria-hidden>
          👨‍👩‍👧
        </span>
        <span className={a.regLinkModeText}>
          <strong>Семья</strong>
          <small>Родитель и один или несколько детей</small>
        </span>
      </button>
      <button
        type="button"
        className={a.regLinkModeBtn}
        onClick={() => onPick("solo")}
      >
        <span className={a.regLinkModeIcon} aria-hidden>
          🏃
        </span>
        <span className={a.regLinkModeText}>
          <strong>Один человек</strong>
          <small>Спортсмен регистрируется сам</small>
        </span>
      </button>
    </div>
  );
}

export default function AuthRegisterByLinkFlow({
  clubName,
  linkInfo,
  backTo,
  backLabel,
  loading,
  error,
  onSubmit,
  footer,
  segment,
}) {
  const [regMode, setRegMode] = useState(null);
  const [step, setStep] = useState(1);
  const [parent, setParent] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [solo, setSolo] = useState({
    fullName: "",
    phone: "",
    email: "",
    date_of_birth: "",
  });
  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);

  const patchParent = (key, val) => setParent((p) => ({ ...p, [key]: val }));
  const patchSolo = (key, val) => setSolo((p) => ({ ...p, [key]: val }));
  const patchChild = (idx, patch) =>
    setChildren((list) =>
      list.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );

  const groupHint = linkInfo?.group?.name
    ? `Группа: ${linkInfo.group.name}`
    : null;

  const pickMode = (mode) => {
    setRegMode(mode);
    setStep(1);
  };

  const step1Family = (e) => {
    e.preventDefault();
    if (!parent.fullName.trim() || !parent.phone.trim()) return;
    setStep(2);
  };

  const finishFamily = (e) => {
    e.preventDefault();
    const childRows = children
      .map((c) => ({
        fullName: c.fullName.trim(),
        date_of_birth: c.date_of_birth || null,
      }))
      .filter((c) => c.fullName);
    if (!childRows.length || childRows.some((c) => !c.date_of_birth)) return;
    onSubmit({
      registrationType: "family",
      fullName: parent.fullName.trim(),
      phone: parent.phone.trim(),
      email: parent.email.trim() || undefined,
      children: childRows,
    });
  };

  const finishSolo = (e) => {
    e.preventDefault();
    if (!solo.fullName.trim() || !solo.phone.trim() || !solo.date_of_birth) return;
    onSubmit({
      registrationType: "solo",
      fullName: solo.fullName.trim(),
      phone: solo.phone.trim(),
      email: solo.email.trim() || undefined,
      dateOfBirth: solo.date_of_birth,
    });
  };

  const step1Valid = parent.fullName.trim() && parent.phone.trim();
  const step2Valid = children.some(
    (c) => c.fullName.trim() && c.date_of_birth,
  );
  const soloValid =
    solo.fullName.trim() && solo.phone.trim() && solo.date_of_birth;

  const soloAge = ageFromDob(solo.date_of_birth);

  let subtitle =
    "Выберите тип заявки — пароль зададите после подтверждения тренером.";
  if (regMode === "family") {
    subtitle = groupHint
      ? `${groupHint}. Укажите телефон родителя и данные детей.`
      : "Укажите телефон родителя и данные детей.";
  } else if (regMode === "solo") {
    subtitle = groupHint
      ? `${groupHint}. Укажите свои контакты и дату рождения.`
      : "Укажите свои контакты и дату рождения.";
  }

  const stepCurrent = !regMode ? 1 : regMode === "family" ? step : 1;
  const stepTotal = !regMode ? 1 : regMode === "family" ? 2 : 1;
  const stepLabels =
    !regMode || regMode === "solo"
      ? ["Заявка"]
      : ["Родитель", "Дети"];

  return (
    <AuthShell
      modern
      wide
      backTo={backTo}
      backLabel={backLabel}
      showSwitcher={false}
      cardTitle={linkInfo?.title || "Заявка в клуб"}
      hint={subtitle}
      clubTag={clubName}
      stepCurrent={stepCurrent}
      stepTotal={stepTotal}
      stepLabels={stepLabels}
      segment={segment}
      footer={
        regMode === "family" && step === 2
          ? footer
          : regMode === "solo"
            ? footer
            : null
      }
    >
      {error ? <div className={a.errorBox}>{error}</div> : null}

      {!regMode ? (
        <ModePicker onPick={pickMode} />
      ) : regMode === "solo" ? (
        <form onSubmit={finishSolo}>
          <button
            type="button"
            className={a.regLinkModeBack}
            onClick={() => setRegMode(null)}
          >
            ← Другой тип заявки
          </button>
          <AuthField
            label="Имя"
            value={solo.fullName}
            onChange={(e) => patchSolo("fullName", e.target.value)}
            required
            autoFocus
          />
          <AuthField
            label="Телефон"
            type="tel"
            value={solo.phone}
            onChange={(e) => patchSolo("phone", e.target.value)}
            autoComplete="tel"
            required
          />
          <AuthField
            label="Email"
            type="email"
            value={solo.email}
            onChange={(e) => patchSolo("email", e.target.value)}
            autoComplete="email"
            hint="Необязательно — для восстановления доступа"
          />
          <AuthField
            label="Дата рождения"
            type="date"
            value={solo.date_of_birth}
            onChange={(e) => patchSolo("date_of_birth", e.target.value)}
            required
            max={new Date().toISOString().slice(0, 10)}
            hint={soloAge != null ? `Возраст: ${soloAge} лет` : undefined}
          />
          <Button type="submit" block size="lg" disabled={loading || !soloValid}>
            {loading ? "Отправка…" : "Отправить заявку"}
          </Button>
        </form>
      ) : step === 1 ? (
        <form onSubmit={step1Family}>
          <button
            type="button"
            className={a.regLinkModeBack}
            onClick={() => setRegMode(null)}
          >
            ← Другой тип заявки
          </button>
          <AuthField
            label="Имя родителя"
            value={parent.fullName}
            onChange={(e) => patchParent("fullName", e.target.value)}
            required
            autoFocus
          />
          <AuthField
            label="Телефон родителя"
            type="tel"
            value={parent.phone}
            onChange={(e) => patchParent("phone", e.target.value)}
            autoComplete="tel"
            required
            hint="Основной контакт семьи в клубе"
          />
          <AuthField
            label="Email"
            type="email"
            value={parent.email}
            onChange={(e) => patchParent("email", e.target.value)}
            autoComplete="email"
            hint="Необязательно — для восстановления доступа"
          />
          <Button type="submit" block size="lg" disabled={!step1Valid}>
            Далее
          </Button>
        </form>
      ) : (
        <form onSubmit={finishFamily}>
          {children.map((child, idx) => {
            const age = ageFromDob(child.date_of_birth);
            return (
              <div key={idx}>
                <AuthField
                  label={idx === 0 ? "Имя ребёнка" : `Ещё ребёнок ${idx + 1}`}
                  value={child.fullName}
                  onChange={(e) => patchChild(idx, { fullName: e.target.value })}
                  required={idx === 0}
                />
                <AuthField
                  label="Дата рождения"
                  type="date"
                  value={child.date_of_birth}
                  onChange={(e) =>
                    patchChild(idx, { date_of_birth: e.target.value })
                  }
                  required={idx === 0}
                  max={new Date().toISOString().slice(0, 10)}
                  hint={age != null ? `Возраст: ${age} лет` : undefined}
                />
              </div>
            );
          })}
          <button
            type="button"
            className={a.footerLinksModern}
            onClick={() => setChildren((list) => [...list, { ...EMPTY_CHILD }])}
          >
            + Добавить ещё ребёнка
          </button>
          <div className={a.actions}>
            <Button
              type="submit"
              block
              size="lg"
              disabled={loading || !step2Valid}
            >
              {loading ? "Отправка…" : "Отправить заявку"}
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(1)}>
              Назад
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
